import { RefObject, useCallback, useEffect, useRef } from 'react'
import { View } from 'react-native'

import { IS_WEB } from '../../constants'

/**
 * Closes the popover when a click occurs outside the content and trigger elements.
 * react-native-popper's shouldCloseOnOutsideClick closes on ANY click, including inside the
 * popover content, so we handle outside-click detection manually.
 */
export const useOutsideClickClose = (
  contentRef: RefObject<View | HTMLElement>,
  triggerRef: RefObject<View | HTMLElement> | undefined,
  isOpen: boolean,
  onClose: () => void,
) => {
  const insideEvents = useRef(new WeakSet<Event>())
  // Portals are outside the DOM container but still bubble through their React owner.
  const onMouseDownCapture = useCallback((event: { nativeEvent: Event }) => {
    insideEvents.current.add(event.nativeEvent)
  }, [])

  useEffect(() => {
    if (!IS_WEB || !isOpen) return
    const listener = (e: MouseEvent) => {
      const contentEl = contentRef.current as unknown as HTMLElement | null
      const triggerEl = triggerRef?.current as unknown as HTMLElement | null
      if (
        insideEvents.current.has(e) ||
        contentEl?.contains(e.target as Node) ||
        triggerEl?.contains(e.target as Node)
      ) {
        return
      }
      onClose()
    }
    document.addEventListener('mousedown', listener)
    return () => document.removeEventListener('mousedown', listener)
  }, [isOpen, onClose, contentRef, triggerRef])

  return onMouseDownCapture
}
